import React, { useState, useEffect, useRef } from 'react';
import FileUpload from './FileUpload';
import { 
  getTicketDetails, 
  addTicketMessage, 
  updateTicketStatus, 
  uploadAttachment,
  getFileUrl,
  markTicketAsRead,
  isImageFile,
  isAdminEmail,
  formatTicketStatus, 
  formatPriority, 
  formatDate 
} from '../utils/supportUtils';

const TicketDetail = ({ ticketId, user, isAdmin, onBack, onTicketUpdated }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isAdminNote, setIsAdminNote] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [adminEmails, setAdminEmails] = useState(new Set());
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Only load if we haven't loaded this ticket before or if ticketId changed
    if (!hasLoadedOnce || !ticket || ticket.id !== ticketId) {
      loadTicketDetails();
    }
  }, [ticketId]);

  useEffect(() => {
    scrollToBottom();
  }, [ticket?.ticket_messages]);

  useEffect(() => {
    // Mark ticket as read when viewing details
    if (ticket && user) {
      markTicketAsRead(ticketId, user.id).then(() => {
        // Trigger a custom event to notify Header component
        window.dispatchEvent(new CustomEvent('ticketRead'));
      });
    }
  }, [ticket, ticketId, user]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkAdminEmails = async (messages) => {
    if (!messages || messages.length === 0) return;

    const uniqueEmails = [...new Set(messages.map(msg => msg.user_email))];
    const adminEmailsSet = new Set();

    for (const email of uniqueEmails) {
      const isAdmin = await isAdminEmail(email);
      if (isAdmin) {
        adminEmailsSet.add(email);
      }
    }

    setAdminEmails(adminEmailsSet);
  };

  const loadTicketDetails = async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await getTicketDetails(ticketId, user.email, isAdmin);
      
      if (fetchError) {
        setError('Failed to load ticket details');
        return;
      }

      setTicket(data);
      setHasLoadedOnce(true);

      // Check which message authors are admins
      if (data.ticket_messages) {
        await checkAdminEmails(data.ticket_messages);
      }
    } catch (err) {
      console.error('Error loading ticket:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!isAdmin) return;

    setUpdatingStatus(true);
    try {
      const { data, error: updateError } = await updateTicketStatus(ticketId, newStatus);
      
      if (updateError) {
        setError('Failed to update ticket status');
        return;
      }

      setTicket(prev => ({ ...prev, status: newStatus, updated_at: data.updated_at }));
      onTicketUpdated?.(data);
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update ticket status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleFileSelect = (files) => {
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const handleFileRemove = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    
    if (!newMessage.trim() && selectedFiles.length === 0) {
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Add the message
      const messageData = {
        ticket_id: ticketId,
        user_id: user.id,
        user_email: user.email,
        message: newMessage.trim() || '(File attachment)',
        is_admin_note: isAdmin && isAdminNote
      };

      const { data: message, error: messageError } = await addTicketMessage(messageData);
      
      if (messageError) {
        setError('Failed to send message');
        return;
      }

      // Upload attachments if any
      if (selectedFiles.length > 0) {
        const uploadPromises = selectedFiles.map(file =>
          uploadAttachment(file, ticketId, message.id, user.id)
        );

        await Promise.allSettled(uploadPromises);
      }

      // Clear form
      setNewMessage('');
      setSelectedFiles([]);
      setIsAdminNote(false);

      // Reload ticket details to show new message
      await loadTicketDetails();

    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadFile = async (attachment) => {
    try {
      const { data: url, error } = await getFileUrl(attachment.file_path);
      
      if (error) {
        console.error('Error getting file URL:', error);
        return;
      }

      // Open file in new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error downloading file:', err);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="status-card">
          <div className="spinner" />
          <div>
            <h3>Loading ticket details</h3>
            <p>Pulling messages and attachments...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <p className="text-text-muted">Ticket not found</p>
          <button onClick={onBack} className="btn-secondary mt-4">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = formatTicketStatus(ticket.status);
  const priorityInfo = formatPriority(ticket.priority);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex items-start justify-between mb-4">
          <button
            onClick={onBack}
            className="flex items-center text-text-muted hover:text-text-light transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to tickets
          </button>
          
          {isAdmin && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-text-muted">Status:</span>
              <select
                value={ticket.status}
                onChange={(e) => handleStatusUpdate(e.target.value)}
                disabled={updatingStatus}
                className="input-field py-1.5 px-2 text-sm"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="reviewing">Reviewing</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-bold text-text-light mb-2">{ticket.title}</h1>
            <div className="flex items-center space-x-4">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${priorityInfo.color}`}>
                {priorityInfo.label}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-text-muted">Created by:</span>
              <span className="text-text-light ml-2">{ticket.user_email}</span>
            </div>
            <div>
              <span className="text-text-muted">Created:</span>
              <span className="text-text-light ml-2">{formatDate(ticket.created_at)}</span>
            </div>
            <div>
              <span className="text-text-muted">Last updated:</span>
              <span className="text-text-light ml-2">{formatDate(ticket.updated_at)}</span>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium text-text-light mb-2">Description:</h3>
            <p className="text-text-muted whitespace-pre-wrap">{ticket.description}</p>
          </div>

          {/* Initial Ticket Attachments */}
          {ticket.ticket_attachments && ticket.ticket_attachments.filter(att => !att.message_id).length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-text-light mb-2">Attachments:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {ticket.ticket_attachments
                  .filter(att => !att.message_id)
                  .map((attachment) => (
                    <AttachmentDisplay key={attachment.id} attachment={attachment} />
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="bg-card-dark border border-border-dark rounded-lg">
        <div className="px-6 py-4 border-b border-border-dark">
          <h2 className="text-lg font-semibold text-text-light">Messages</h2>
        </div>

        <div className="max-h-96 overflow-y-auto p-6 space-y-4">
          {ticket.ticket_messages && ticket.ticket_messages.length > 0 ? (
            ticket.ticket_messages.map((message) => (
              <div key={message.id} className="space-y-2">
                <div className={`p-4 rounded-lg ${
                  message.is_admin_note 
                    ? 'bg-purple-900/20 border border-purple-500/30' 
                    : 'bg-primary-dark border border-border-dark'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span className={`text-sm font-medium ${
                        adminEmails.has(message.user_email) 
                          ? 'text-[#0f0]' 
                          : 'text-text-light'
                      }`}>
                        {adminEmails.has(message.user_email) ? 'Support' : message.user_email}
                      </span>
                      {message.is_admin_note && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Admin Note
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-text-muted">
                      {formatDate(message.created_at)}
                    </span>
                  </div>
                  <p className="text-text-light whitespace-pre-wrap">{message.message}</p>
                  
                  {/* Message Attachments */}
                  {message.ticket_attachments && message.ticket_attachments.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-text-light mb-2">Attachments:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {message.ticket_attachments.map((attachment) => (
                          <AttachmentDisplay key={attachment.id} attachment={attachment} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <p className="text-text-muted text-center py-4">No messages yet</p>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Reply Form */}
      {ticket.status !== 'resolved' && (
        <div className="bg-card-dark border border-border-dark rounded-lg p-6">
          <h3 className="text-lg font-semibold text-text-light mb-4">
            {isAdmin ? 'Reply to Ticket' : 'Add Message'}
          </h3>

          <form onSubmit={handleSubmitMessage} className="space-y-4">
            {/* Admin Note Toggle */}
            {isAdmin && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="adminNote"
                  checked={isAdminNote}
                  onChange={(e) => setIsAdminNote(e.target.checked)}
                  className="h-4 w-4 text-accent-green focus:ring-accent-green border-border-dark rounded"
                />
                <label htmlFor="adminNote" className="ml-2 text-sm text-text-light">
                  Admin note (not visible to user)
                </label>
              </div>
            )}

            {/* Message Input */}
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={isAdmin && isAdminNote ? "Add an internal note..." : "Type your message..."}
              rows={4}
              className="w-full px-4 py-3 bg-primary-dark border border-border-dark rounded-lg text-text-light placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-accent-green focus:border-transparent resize-vertical"
              disabled={submitting}
            />

            {/* File Upload */}
            <div>
              <FileUpload
                onFileSelect={handleFileSelect}
                onFileRemove={handleFileRemove}
                selectedFiles={selectedFiles}
                disabled={submitting}
                multiple={true}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting || (!newMessage.trim() && selectedFiles.length === 0)}
                className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting && (
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                <span>{submitting ? 'Sending...' : 'Send Message'}</span>
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

// AttachmentDisplay component for showing thumbnails and file links
const AttachmentDisplay = ({ attachment }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isImageFile(attachment.mime_type)) {
      loadImageUrl();
    }
  }, [attachment]);

  const loadImageUrl = async () => {
    setLoading(true);
    try {
      const { data: url } = await getFileUrl(attachment.file_path);
      if (url) {
        setImageUrl(url);
      }
    } catch (error) {
      console.error('Error loading image URL:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    try {
      const { data: url, error } = await getFileUrl(attachment.file_path);
      
      if (error) {
        console.error('Error getting file URL:', error);
        return;
      }

      // Open file in new tab
      window.open(url, '_blank');
    } catch (err) {
      console.error('Error opening file:', err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isImageFile(attachment.mime_type)) {
    return (
      <div 
        onClick={handleClick}
        className="relative group cursor-pointer bg-card-dark border border-border-dark rounded-lg overflow-hidden hover:border-accent-green transition-colors"
      >
        {loading ? (
          <div className="aspect-square flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-green"></div>
          </div>
        ) : imageUrl ? (
          <div className="aspect-square">
            <img 
              src={imageUrl} 
              alt={attachment.file_name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
              <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
          </div>
        ) : (
          <div className="aspect-square flex items-center justify-center">
            <svg className="w-8 h-8 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-2">
          <p className="text-xs truncate">{attachment.file_name}</p>
          <p className="text-xs text-gray-300">{formatFileSize(attachment.file_size)}</p>
        </div>
      </div>
    );
  }

  // Non-image files
  return (
    <div 
      onClick={handleClick}
      className="flex items-center space-x-3 p-3 bg-card-dark border border-border-dark rounded-lg hover:border-accent-green transition-colors cursor-pointer"
    >
      <div className="flex-shrink-0">
        {attachment.mime_type === 'application/pdf' ? (
          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        ) : (
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-light truncate">{attachment.file_name}</p>
        <p className="text-xs text-text-muted">{formatFileSize(attachment.file_size)}</p>
      </div>
      <div className="flex-shrink-0">
        <svg className="w-5 h-5 text-accent-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </div>
    </div>
  );
};

export default TicketDetail;
